import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { Toaster } from "sonner";
import "@/App.css";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import DevicesList from "@/pages/DevicesList";
import DeviceWizard from "@/pages/DeviceWizard";
import DeviceDetail from "@/pages/DeviceDetail";
import Alerts from "@/pages/Alerts";
import Pricing from "@/pages/Pricing";
import Settings from "@/pages/Settings";
import VitaCyt from "@/pages/VitaCyt";
import OCRPage from "@/pages/OCRPage";
import CAPA from "@/pages/CAPA";
import CAPADetail from "@/pages/CAPADetail";
import Deviations from "@/pages/Deviations";
import ChangeControl from "@/pages/ChangeControl";
import PatientAdvisory from "@/pages/PatientAdvisory";
import Training from "@/pages/Training";
import Documents from "@/pages/Documents";
import RiskManagement from "@/pages/RiskManagement";
import RiskRegister from "@/pages/RiskRegister";
import RiskTrendPage from "@/pages/RiskTrendPage";
import NBPortfolio from "@/pages/NBPortfolio";
import FailureTraceback from "@/pages/FailureTraceback";
import AIMLGovernance from "@/pages/AIMLGovernance";
import Admin from "@/pages/Admin";
import GapDetector from "@/pages/GapDetector";
import Complaints from "@/pages/Complaints";
import Clinical from "@/pages/Clinical";
import Studies from "@/pages/Studies";
import FormBuilder from "@/pages/FormBuilder";
import DataEntry from "@/pages/DataEntry";
import Queries from "@/pages/Queries";
import Onboarding from "@/pages/Onboarding";
import OnboardingSnapshot from "@/pages/OnboardingSnapshot";
import InspectionPortal from "@/pages/InspectionPortal";
import AcceptInvite from "@/pages/AcceptInvite";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AuthCallback from "@/pages/AuthCallback";
import Cybersecurity from "@/pages/Cybersecurity";
import Submissions510k from "@/pages/Submissions510k";
import AdverseEvents from "@/pages/AdverseEvents";
import CER from "@/pages/CER";
import ConsentForms from "@/pages/ConsentForms";
import ConsentSign from "@/pages/ConsentSign";
import IEC62304 from "@/pages/IEC62304";
import IEC62366 from "@/pages/IEC62366";
import TrainingMatrix from "@/pages/TrainingMatrix";
import RegulatoryReadiness from "@/pages/RegulatoryReadiness";
import GlobalStandards from "@/pages/GlobalStandards";
import UDIRegistry from "@/pages/UDIRegistry";
import QualityKPIs from "@/pages/QualityKPIs";
import Traceability from "@/pages/Traceability";
import NIS2 from "@/pages/NIS2";
import ProtectedRoute from "@/components/ProtectedRoute";

function AppRouter() {
  const location = useLocation();
  // Handle Google OAuth callback (session_id in hash) synchronously
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/inspect/:token" element={<InspectionPortal />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/devices" element={<ProtectedRoute><DevicesList /></ProtectedRoute>} />
      <Route path="/devices/new" element={<ProtectedRoute><DeviceWizard /></ProtectedRoute>} />
      <Route path="/devices/:id" element={<ProtectedRoute><DeviceDetail /></ProtectedRoute>} />
      <Route path="/devices/:id/risk-trend" element={<ProtectedRoute><RiskTrendPage /></ProtectedRoute>} />
      <Route path="/audit/nb-portfolio" element={<ProtectedRoute><NBPortfolio /></ProtectedRoute>} />
      <Route path="/traceability/traceback" element={<ProtectedRoute><FailureTraceback /></ProtectedRoute>} />
      <Route path="/alerts" element={<ProtectedRoute><Alerts /></ProtectedRoute>} />
      <Route path="/vitacyt" element={<ProtectedRoute><VitaCyt /></ProtectedRoute>} />
      <Route path="/ocr" element={<ProtectedRoute><OCRPage /></ProtectedRoute>} />
      <Route path="/capa" element={<ProtectedRoute><CAPA /></ProtectedRoute>} />
      <Route path="/capa/:id" element={<ProtectedRoute><CAPADetail /></ProtectedRoute>} />
      <Route path="/deviations" element={<ProtectedRoute><Deviations /></ProtectedRoute>} />
      <Route path="/changes" element={<ProtectedRoute><ChangeControl /></ProtectedRoute>} />
      <Route path="/complaints" element={<ProtectedRoute><Complaints /></ProtectedRoute>} />
      <Route path="/clinical" element={<ProtectedRoute><Clinical /></ProtectedRoute>} />
      <Route path="/clinical/studies" element={<ProtectedRoute><Studies /></ProtectedRoute>} />
      <Route path="/clinical/forms" element={<ProtectedRoute><FormBuilder /></ProtectedRoute>} />
      <Route path="/clinical/data-entry" element={<ProtectedRoute><DataEntry /></ProtectedRoute>} />
      <Route path="/clinical/queries" element={<ProtectedRoute><Queries /></ProtectedRoute>} />
      <Route path="/clinical/adverse-events" element={<ProtectedRoute><AdverseEvents /></ProtectedRoute>} />
      <Route path="/clinical/cer" element={<ProtectedRoute><CER /></ProtectedRoute>} />
      <Route path="/clinical/consent" element={<ProtectedRoute><ConsentForms /></ProtectedRoute>} />
      <Route path="/consent/sign/:token" element={<ConsentSign />} />
      <Route path="/cybersecurity" element={<ProtectedRoute><Cybersecurity /></ProtectedRoute>} />
      <Route path="/submissions" element={<ProtectedRoute><Submissions510k /></ProtectedRoute>} />
      <Route path="/iec62304" element={<ProtectedRoute><IEC62304 /></ProtectedRoute>} />
      <Route path="/iec62366" element={<ProtectedRoute><IEC62366 /></ProtectedRoute>} />
      <Route path="/training/matrix" element={<ProtectedRoute><TrainingMatrix /></ProtectedRoute>} />
      <Route path="/readiness" element={<ProtectedRoute><RegulatoryReadiness /></ProtectedRoute>} />
      <Route path="/quality-kpis" element={<ProtectedRoute><QualityKPIs /></ProtectedRoute>} />
      <Route path="/traceability" element={<ProtectedRoute><Traceability /></ProtectedRoute>} />
      <Route path="/nis2" element={<ProtectedRoute><NIS2 /></ProtectedRoute>} />
      <Route path="/global-standards" element={<ProtectedRoute><GlobalStandards /></ProtectedRoute>} />
      <Route path="/udi" element={<ProtectedRoute><UDIRegistry /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/onboarding/snapshot/:projectId" element={<ProtectedRoute><OnboardingSnapshot /></ProtectedRoute>} />
      <Route path="/patient-advisory" element={<ProtectedRoute><PatientAdvisory /></ProtectedRoute>} />
      <Route path="/training" element={<ProtectedRoute><Training /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/risk" element={<ProtectedRoute><RiskManagement /></ProtectedRoute>} />
      <Route path="/risk-register" element={<ProtectedRoute><RiskRegister /></ProtectedRoute>} />
      <Route path="/ai-governance" element={<ProtectedRoute><AIMLGovernance /></ProtectedRoute>} />
      <Route path="/gap-detector" element={<ProtectedRoute><GapDetector /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
